// Active-filter banner for the meetings tab. Mirrors the deliverable
// equivalent: shows a single-line summary of every active chip, search
// term, and the Include archived toggle, with a Clear all button. Renders
// whenever any filter or search is active so users can always reset.

import type {
  MeetingCadenceFilter,
  MeetingDateRangeFilter,
  MeetingFilterChipBarFilters,
  MeetingMinutesFilter,
} from "./MeetingFilterChipBar";
import type { MeetingDerivedStatus } from "../../../../types";

export interface MeetingActiveFilterSummaryProps {
  filters: MeetingFilterChipBarFilters;
  search: string;
  onClearAll: () => void;
}

const CADENCE_LABEL: Record<MeetingCadenceFilter, string> = {
  recurring: "Recurring",
  "one-off": "One-off",
};

const STATUS_LABEL: Record<MeetingDerivedStatus, string> = {
  scheduled: "Scheduled",
  "due-soon": "Due soon",
  overdue: "Overdue",
  held: "Held",
  cancelled: "Cancelled",
};

const MINUTES_LABEL: Record<MeetingMinutesFilter, string> = {
  "with-minutes": "With minutes",
  "awaiting-minutes": "Awaiting minutes",
  "not-applicable": "Not applicable",
};

const DATE_RANGE_LABEL: Record<MeetingDateRangeFilter, string> = {
  "this-week": "This week",
  "next-30-days": "Next 30 days",
  "past-30-days": "Past 30 days",
  "all-time": "All time",
};

function joinList<T extends string>(
  values: T[],
  labels: Record<T, string>,
): string {
  return values.map((v) => labels[v]).join(", ");
}

function hasAnyActive(
  filters: MeetingFilterChipBarFilters,
  search: string,
): boolean {
  if (filters.cadence.length > 0) return true;
  if (filters.status.length > 0) return true;
  if (filters.minutes.length > 0) return true;
  if (filters.dateRange !== "all-time") return true;
  if (filters.includeArchived) return true;
  if (search.trim().length > 0) return true;
  return false;
}

export default function MeetingActiveFilterSummary({
  filters,
  search,
  onClearAll,
}: MeetingActiveFilterSummaryProps) {
  if (!hasAnyActive(filters, search)) return null;

  const parts: string[] = [];
  if (filters.cadence.length > 0) {
    parts.push(`Cadence: ${joinList(filters.cadence, CADENCE_LABEL)}`);
  }
  if (filters.status.length > 0) {
    parts.push(`Status: ${joinList(filters.status, STATUS_LABEL)}`);
  }
  if (filters.minutes.length > 0) {
    parts.push(`Minutes: ${joinList(filters.minutes, MINUTES_LABEL)}`);
  }
  if (filters.dateRange !== "all-time") {
    parts.push(`Date range: ${DATE_RANGE_LABEL[filters.dateRange]}`);
  }
  if (filters.includeArchived) {
    parts.push("Include archived");
  }
  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    parts.push(`Search: '${trimmedSearch}'`);
  }

  return (
    <div
      data-testid="meeting-active-filter-summary"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-caption text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300"
    >
      <span>{parts.join(" · ")}</span>
      <button
        type="button"
        onClick={onClearAll}
        className="text-caption font-medium text-brand-600 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400"
      >
        Clear all
      </button>
    </div>
  );
}
